import { useEffect, useState } from 'react';
import { getEsimCountriesApi } from '../api/esimApi';

/**
 * Cached fetch hook for the list of eSIM-supported countries. The list is
 * stable per session, so we memoise it on `window` to avoid re-fetching on
 * every page mount (search form, plans page, voucher page).
 */
const CACHE_KEY = '__esim_countries_cache__';

const useEsimCountries = () => {
  const [countries, setCountries] = useState(
    typeof window !== 'undefined' && window[CACHE_KEY] ? window[CACHE_KEY] : [],
  );
  const [loading, setLoading] = useState(
    !(typeof window !== 'undefined' && window[CACHE_KEY]),
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (
      typeof window !== 'undefined' &&
      window[CACHE_KEY] &&
      window[CACHE_KEY].length > 0
    ) {
      setCountries(window[CACHE_KEY]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getEsimCountriesApi();
        if (cancelled) return;
        if (typeof window !== 'undefined') {
          window[CACHE_KEY] = data;
        }
        setCountries(data || []);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    countries,
    options: countries,
    loading,
    error,
  };
};

export default useEsimCountries;
